import { z } from 'zod';

/**
 * Frontend env schema for the public onboarding bundle — PURE + testable
 * (no `import.meta`, no eager validation; that lives in `env.ts`).
 *
 * Why the tolerant optional: Vite inlines a `.env.production` line written as
 * `VITE_FOO=` as the EMPTY STRING (not `undefined`). A plain
 * `z.string().min(1).optional()` accepts `undefined` but REJECTS `""`
 * (present-but-empty) — so a blank optional var crashed the whole bundle at
 * boot, even with the resumable flag OFF (incident 2026-06-15). Treating `''`
 * as "unset" lets a dark bundle boot, while the `superRefine` still requires the
 * Firebase config when the resumable flag is ON.
 */
export const optionalNonEmptyString = z.preprocess(
  (value) => (value === '' ? undefined : value),
  z.string().min(1).optional(),
);

export const frontendEnvSchema = z
  .object({
    // Required — the form posts to `${VITE_API_BASE_URL}/api/public/...`.
    VITE_API_BASE_URL: z.string().url(),

    // Required — Cloudflare Turnstile widget site key.
    VITE_TURNSTILE_SITE_KEY: z.string().min(1),

    // Optional — when missing the AddressAutocomplete renders nothing.
    VITE_HERE_API_KEY: optionalNonEmptyString,

    // PR-ONB-RESUME-1B — resumable-area FE rollout flag (default off).
    VITE_ONBOARDING_RESUMABLE_ENABLED: z.string().optional(),

    // Firebase Web SDK config — public by design. Required ONLY when the
    // resumable flag is on (superRefine below). A blank value is tolerated when
    // the flag is off (the Firebase client is lazy-loaded behind the flag).
    VITE_FIREBASE_API_KEY: optionalNonEmptyString,
    VITE_FIREBASE_AUTH_DOMAIN: optionalNonEmptyString,
    VITE_FIREBASE_PROJECT_ID: optionalNonEmptyString,
  })
  .superRefine((data, ctx) => {
    if (data.VITE_ONBOARDING_RESUMABLE_ENABLED !== 'true') return;
    for (const key of [
      'VITE_FIREBASE_API_KEY',
      'VITE_FIREBASE_AUTH_DOMAIN',
      'VITE_FIREBASE_PROJECT_ID',
    ] as const) {
      if (!data[key]) {
        ctx.addIssue({
          code: 'custom',
          path: [key],
          message: `${key} richiesto quando VITE_ONBOARDING_RESUMABLE_ENABLED=true`,
        });
      }
    }
  });

export type AppEnv = z.infer<typeof frontendEnvSchema>;

/** Pure parse — used by `env.ts` (eager) and by the unit tests. */
export function parseFrontendEnv(raw: unknown) {
  return frontendEnvSchema.safeParse(raw);
}
